import Vue from 'vue';
import Router from 'vue-router';
import Home from '@/views/Home.vue';
import Login from '@/views/Login.vue';
import { ContactProfileEnum } from '@gid/models/dist/entities/contact-profile-enum';
import Entities from '../entities';
import { DataUiBlueprints } from '@gid/models';

Vue.use(Router);

/**
 * Where a contact confined to a holding profile is sent instead of Home.
 *
 * Home is `contactProfiles: true` — open to any authenticated user — so without
 * this a holding contact lands on a dashboard whose every panel 403s. Route-level
 * access itself is enforced elsewhere: App.vue only renders `<router-view>` when
 * `checkRouteAccess` passes, so a disallowed URL shows the no-access panel rather
 * than the view. This is purely about landing somewhere useful.
 */
export const HOLDING_LANDING_ROUTE = { name: 'partners-map' };

// Where a share link asked to land, remembered across the login hand-off.
const SHARE_TARGET_KEY = 'gid.shareTarget';

/**
 * Only same-origin absolute paths are ever stored or replayed.
 *
 * The value reaches us from a URL query, so without this it is an open redirect:
 * `//evil.example` and `https://evil.example` are both valid `router.push`
 * arguments that would send a freshly authenticated holding contact off-site.
 */
function isSafeInternalPath(path) {
  return (
    typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')
  );
}

export function rememberShareTarget(path) {
  try {
    if (isSafeInternalPath(path)) {
      window.sessionStorage.setItem(SHARE_TARGET_KEY, path);
    }
  } catch (e) {
    // Private-mode sessionStorage throws; the fallback route still works.
  }
}

/**
 * Where to send a holding contact who has landed somewhere they cannot use.
 *
 * A share link carries the view it was minted for (`?redirect=…`), and there is
 * now more than one holding-visible page — so a hardcoded landing route sends
 * every recipient to the partner map regardless of which report the link was
 * created from.
 *
 * The redirect query alone is not enough: the login hand-off bounces through
 * `/login` and re-boots the app, which drops the query before the redirect is
 * applied. Remembering the target for the session is what survives that.
 */
export function holdingLandingRoute() {
  try {
    const target = window.sessionStorage.getItem(SHARE_TARGET_KEY);
    if (isSafeInternalPath(target)) return target;
  } catch (e) {
    // fall through to the default
  }
  return HOLDING_LANDING_ROUTE;
}

/* Routes definition specification
 *
 * For each route access is defined via the meta setting
 * `auth`, which could contain `contactProfiles`, `accountProfiles` and `accountStatuses`.
 * If `auth` is present, the user must be authenticated.
 * `contactProfiles` recognizes the following values:
 * - true: requires authenticated user without contact profile restrictions
 * - array of strings: requires authenticated with one of the specified contact profiles
 */

export default new Router({
  mode: 'history',
  linkActiveClass: 'active',
  linkExactActiveClass: 'active',
  routes: [
    {
      path: '/',
      name: 'home',
      meta: {
        auth: {
          contactProfiles: true,
        },
      },
      component: Home,
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
    },
    {
      path: '/tools',
      name: 'tools',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      redirect: { name: 'email-tester' },
    },
    {
      path: '/email-queues',
      name: 'email-queues',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/tools/QueueDashboard.vue'),
      props: { route: '/email-api/queues-monitor' },
    },
    {
      path: '/jobs-service-queues',
      name: 'jobs-service-queues',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/tools/QueueDashboard.vue'),
      props: { route: '/jobs-api/queues-monitor' },
    },
    {
      path: '/crud-queues',
      name: 'crud-queues',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/tools/QueueDashboard.vue'),
      props: { route: '/data-api/queues-monitor' },
    },
    {
      path: '/documents-service-queues',
      name: 'documents-service-queues',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/tools/QueueDashboard.vue'),
      props: { route: '/documents-api/queues-monitor' },
    },
    {
      path: '/email-tester',
      name: 'email-tester',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/tools/EmailTester.vue'),
    },
    {
      path: '/jobs/create',
      name: 'jobs-create',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@gid/vue-common/components/JobCreate.vue'),
      props: { context: 'admin' },
    },
    {
      path: '/jobs/create-alternative',
      name: 'jobs-create-alternative',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@gid/vue-common/components/JobFollowup.vue'),
      props: { context: 'admin', alternative: true },
    },
    {
      path: '/jobs/followup',
      name: 'job-followup',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@gid/vue-common/components/JobFollowup.vue'),
      props: { context: 'admin' },
    },
    {
      path: '/jobs/reclamation',
      name: 'job-reclamation',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@gid/vue-common/components/JobFollowup.vue'),
      props: { context: 'admin', reclamation: true },
    },
    {
      path: '/jobs/:job_id?',
      name: 'jobs',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/jobs/Jobs.vue'),
      props: true,
    },
    {
      path: '/tickets',
      name: 'tickets',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/tickets/TicketsConsole.vue'),
      props: true,
    },
    {
      path: '/partners',
      name: 'partners',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
            // One of the two holding pages — see HOLDING_ROUTES below.
            ContactProfileEnum.S4E_HOLDING,
          ],
        },
      },
      component: () => import('@/views/partners/Partners.vue'),
    },
    {
      path: '/partners/map',
      name: 'partners-map',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
            // One of the two holding pages — see HOLDING_ROUTES below.
            ContactProfileEnum.S4E_HOLDING,
          ],
        },
      },
      component: () => import('@/views/partners/PartnerMap.vue'),
    },
    {
      path: '/s4c/utilization',
      name: 's4c-utilization',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
            // One of the holding pages — see HOLDING_ROUTES below.
            ContactProfileEnum.S4E_HOLDING,
          ],
        },
      },
      component: () => import('@/views/s4c/S4cUtilization.vue'),
    },
    {
      path: '/brands',
      name: 'brands',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/brands/Brands.vue'),
    },
    {
      path: '/accounting',
      name: 'accounting',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      redirect: { name: 'accounting-documents' },
    },
    {
      path: '/accounting/drafts',
      name: 'accounting-drafts',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/accounting/Drafts.vue'),
    },
    {
      path: '/accounting/recheck-drafts',
      name: 'accounting-recheck-drafts',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/accounting/RecheckDrafts.vue'),
    },
    {
      path: '/accounting/documents/:documentId?',
      name: 'accounting-documents',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
            ContactProfileEnum.EXTERNAL_TAX_OFFICE,
          ],
        },
      },
      component: () => import('@/views/accounting/Documents.vue'),
      props: true,
    },
    {
      path: '/accounting/payments/:paymentId?',
      name: 'accounting-payments',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
            ContactProfileEnum.EXTERNAL_TAX_OFFICE,
          ],
        },
      },
      component: () => import('@/views/accounting/Payments.vue'),
      props: true,
    },
    {
      path: '/accounting/documents-balances',
      name: 'documents-balances',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/accounting/DocumentsBalances.vue'),
    },
    {
      path: '/accounting/aging',
      name: 'accounting-aging',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/accounting/AccountsAging.vue'),
    },
    // Same guard as the GID aging next door. The endpoint behind this one is
    // additionally closed to the confined holding profiles: open receivables and
    // payables name every customer and supplier and what they owe.
    {
      path: '/accounting/s4c-aging',
      name: 'accounting-s4c-aging',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/accounting/S4cAging.vue'),
    },
    // Revenue by customer on both sides of the merger -- same guard as the two
    // aging reports, and the endpoint likewise denies the confined profiles.
    {
      path: '/accounting/revenue-funnel',
      name: 'accounting-revenue-funnel',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/accounting/RevenueFunnel.vue'),
    },
    {
      path: '/accounting/configuration',
      name: 'accounting-configuration',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/accounting/Configuration.vue'),
    },
    {
      path: '/accounting/exports',
      name: 'accounting-exports',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/accounting/Exports.vue'),
    },
    {
      path: '/order-intake-forms/questions-new',
      name: 'questions-new',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/order-intake-forms/QuestionsNew.vue'),
    },
    {
      path: '/data',
      name: 'data',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.CUSTOMER_MANAGEMENT,
            ContactProfileEnum.PARTNER_ONBOARDING,
            ContactProfileEnum.BRAND_MANAGEMENT,
            ContactProfileEnum.GID,
            ContactProfileEnum.PARTNER_MANAGEMENT,
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/data/Home.vue'),
    },
    {
      path: '/data/:entity',
      name: 'data-entity',
      meta: {
        authContactProfiles: (route, profiles) => {
          const entity = Entities[route.params.entity];
          if (!entity?.permissions) {
            return false;
          }
          return entity.permissions.some((p) => profiles.includes(p));
        },
      },
      component: () => import('@/components/crud/Page.vue'),
      props: true,
    },
    {
      path: '/data-ui/:entity/:entityIdsList?',
      name: 'data-ui-entity',
      meta: {
        authContactProfiles: (route, profiles) => {
          const blueprint = DataUiBlueprints.get(route.params.entity);
          return blueprint?.permissions.read?.some((p) => profiles.includes(p));
        },
      },
      component: () => import('@/components/data-ui/CrudEntityEditor.vue'),
      props: true,
    },
    {
      path: '/rating-admin',
      name: 'rating-admin',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () => import('@/views/integrations/Rating.vue'),
    },
    {
      path: '/order-intake-forms-admin',
      name: 'order-intake-forms-admin',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      component: () =>
        import('@/views/integrations/OrderIntakeFormsBackoffice.vue'),
    },
    {
      path: '/dynamics-migration-plan',
      name: 'dynamics-migration-plan',
      meta: {
        auth: {
          contactProfiles: [ContactProfileEnum.PRODUCT_POWER_USER],
        },
      },
      component: () => import('@/views/integrations/DynamicsMigrationPlan.vue'),
    },
    {
      path: '/product-orders',
      name: 'product-orders',
      meta: {
        auth: {
          contactProfiles: [
            ContactProfileEnum.FINANCE,
            ContactProfileEnum.FINANCE_POWER_USER,
            ContactProfileEnum.PRODUCT,
            ContactProfileEnum.PRODUCT_POWER_USER,
          ],
        },
      },
      redirect: { name: 'product-orders-pending' },
      component: () => import('@/views/product-orders/ProductOrders.vue'),
      children: [
        {
          path: 'pending',
          name: 'product-orders-pending',
          meta: {
            auth: {
              contactProfiles: [
                ContactProfileEnum.GID,
                ContactProfileEnum.FINANCE,
                ContactProfileEnum.FINANCE_POWER_USER,
                ContactProfileEnum.PRODUCT,
                ContactProfileEnum.PRODUCT_POWER_USER,
              ],
            },
          },
          component: () =>
            import('@/views/product-orders/ProductOrdersPending.vue'),
        },
        {
          path: 'ordered',
          name: 'product-orders-ordered',
          meta: {
            auth: {
              contactProfiles: [
                ContactProfileEnum.GID,
                ContactProfileEnum.FINANCE,
                ContactProfileEnum.FINANCE_POWER_USER,
                ContactProfileEnum.PRODUCT,
                ContactProfileEnum.PRODUCT_POWER_USER,
              ],
            },
          },
          component: () =>
            import('@/views/product-orders/ProductOrdersOrdered.vue'),
        },
      ],
    },
    {
      // Read-only WhatsApp inbox (GID-2697). The gate here hides the nav item
      // too (App.vue derives visibility from canAccessRoute), but it is an
      // affordance only — gid-crud re-checks the GID profile on every request
      // and on the websocket handshake.
      path: '/whatsapp',
      name: 'whatsapp',
      meta: {
        auth: {
          contactProfiles: [ContactProfileEnum.GID],
        },
      },
      component: () => import('@/views/whatsapp/WhatsApp.vue'),
    },
    {
      path: '*',
      redirect: (to) => ({
        path: '/',
        query: { notFound: to.fullPath },
      }),
    },
  ],
});
